import { CONFIG_ETATS_TICKET, TicketState } from './ticket-state';
import { EtatService } from './etat';
import type { Etat } from './EntiteProjet';

const FR = 'fr-FR';

/** Normalise une valeur en `Date` - usage interne uniquement. */
function toDate(valeur: string | Date): Date {
  return typeof valeur === 'string' ? new Date(valeur) : valeur;
}

type IndicateursEtat = Pick<Etat, 'hors_ligne' | 'arret' | 'tickets_avec_pertes' | 'tickets_sans_pertes'>;

/** Les indicateurs API priment sur son niveau, dont les codes peuvent différer des codes internes. */
function niveauStatut(etat?: number | IndicateursEtat | null): number | undefined {
  return typeof etat === 'object' && etat !== null
    ? EtatService.calculerNiveau(etat)
    : etat ?? undefined;
}

/** Libellé de l'état de l'installation ; accepte aussi un niveau interne. */
export function statusLabel(etat?: number | IndicateursEtat | null): string {
  const niveau = niveauStatut(etat);
  if (niveau == null) return 'Non communiqué';
  return EtatService.NIVEAU_INFOS[niveau]?.label ?? 'Non communiqué';
}

/**
 * Classe CSS du badge, déduite des indicateurs ou d'un niveau interne.
 * Source : `EtatService.NIVEAU_INFOS`. Retourne `'nc'` pour un niveau inconnu.
 * Utilisé dans : `installation-etat-panel`, `installation` (page), `silo-parc`.
 */
export function statusClass(etat?: number | IndicateursEtat | null): string {
  const niveau = niveauStatut(etat);
  if (niveau == null) return 'nc';
  return EtatService.NIVEAU_INFOS[niveau]?.classe ?? 'nc';
}

export type TicketColumnState = typeof TicketState[keyof typeof TicketState];

/**
 * Libellé textuel de l'état d'un **Ticket** dans le kanban.
 * Utilisé dans : `ticket-detail` (page).
 */
export function ticketStatusLabel(state: TicketColumnState): string {
  return CONFIG_ETATS_TICKET[state].ticketLabel;
}

/**
 * Classe CSS du badge d'état d'un **Ticket** dans le kanban.
 * Utilisé dans : `ticket-detail` (page).
 */
export function ticketStatusClass(state: TicketColumnState): string {
  return CONFIG_ETATS_TICKET[state].statusClass;
}

/**
 * Traduit le code numérique API d'un état de ticket ('1'...'4') en libellé lisible.
 * Utilisé dans : `ticket-evenements`, `ticket-detail` (page).
 */
export function ticketCodeEtatLabel(code: string): string {
  const state = (Object.keys(CONFIG_ETATS_TICKET) as TicketColumnState[])
    .find((ticketState) => CONFIG_ETATS_TICKET[ticketState].etat === code);

  return state ? CONFIG_ETATS_TICKET[state].ticketLabel : code;
}

/**
 * Formate une date de **Ticket** (`Ticket.date`) en "lun. 5 janv.".
 * Retourne `'Date inconnue'` si la valeur est absente ou invalide.
 * Utilisé dans : `tickets` (page).
 */
export function formaterDateAvecJour(valeur?: string): string {
  if (!valeur) return 'Date inconnue';
  const date = new Date(valeur);
  if (Number.isNaN(date.getTime())) return valeur;
  const afficherAnnee = date.getFullYear() !== new Date().getFullYear();
  return new Intl.DateTimeFormat(FR, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    ...(afficherAnnee ? { year: 'numeric' as const } : {}),
  }).format(date);
}

/** Formate une date complète en français, par exemple "mercredi 26 août 2026". */
export function formaterDateLongueAvecJour(valeur: string | Date): string {
  const date = toDate(valeur);
  if (Number.isNaN(date.getTime())) return typeof valeur === 'string' ? valeur : 'Date inconnue';

  return new Intl.DateTimeFormat(FR, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

/**
 * Formate l'heure d'un **TicketEvenement** (`TicketEvenement.date`) en "HH:MM".
 * Utilisé dans : `ticket-evenements`.
 */
export function formaterHeure(valeur: string | Date): string {
  return new Intl.DateTimeFormat(FR, { hour: '2-digit', minute: '2-digit' }).format(toDate(valeur));
}

/** Affiche les heures de début et de fin d'une intervention. */
export function formaterHorairesIntervention(debut?: string | null, fin?: string | null): string {
  const heure = (valeur?: string | null): string | null =>
    valeur && !Number.isNaN(new Date(valeur).getTime()) ? formaterHeure(valeur) : null;
  const heureDebut = heure(debut);
  const heureFin = heure(fin);
  if (!heureDebut && !heureFin) return 'Non renseigné';
  return `${heureDebut ?? '—'} – ${heureFin ?? '—'}`;
}

/**
 * Formate une date-heure d'un **TicketEvenement** (`TicketEvenement.date`) en "5 janv. 2025, 14:30".
 * Utilisé dans : `ticket-evenements`, `ticket-detail` (page).
 */
export function formaterDateHeure(valeur: string | Date): string {
  return new Intl.DateTimeFormat(FR, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(toDate(valeur));
}

/**
 * Formate une date en "05/01/2025".
 * Utilisé pour les dates d'**Installation** et de plage dans `installation` (page).
 * Aussi base de `formaterPlage` et `formaterDateNumeriquePlusHeure`.
 */
export function formaterDateNumerique(valeur: string | Date): string {
  return new Intl.DateTimeFormat(FR, { day: '2-digit', month: '2-digit', year: 'numeric' }).format(toDate(valeur));
}

/**
 * Formate la date de dernière mise à jour d'un état d'**Installation**
 * (`Installation.etat.date`) en "05/01/2025, 14:30".
 * Utilisé dans : `installation-etat-panel`.
 */
export function formaterDateNumeriquePlusHeure(valeur: string | Date): string {
  return new Intl.DateTimeFormat(FR, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(toDate(valeur));
}

/**
 * Formate la date de mise en service d'une **Installation** en "5 janvier 2025".
 * Utilisé dans : `installation` (page), `parc-production-cumulee`.
 */
export function formaterDateLongue(valeur: string | Date): string {
  return toDate(valeur).toLocaleDateString(FR, { day: 'numeric', month: 'long', year: 'numeric' });
}

/**
 * Formate une plage de dates d'un **Parc** ou d'une **Installation** en "Du 05/01/2025 au 31/01/2025".
 * Utilisé dans : `installation` (page), `parc-production-cumulee`, `installation-production-cumulee-panel`.
 */
export function formaterPlage(debut: Date, fin: Date): string {
  return `Du ${formaterDateNumerique(debut)} au ${formaterDateNumerique(fin)}`;
}

export type PalierUnite = { diviseur: number; unite: string };

/**
 * Formate une valeur numérique en choisissant automatiquement le plus grand palier d'unité
 * applicable (ex. kWh -> MWh -> GWh -> TWh), puis en formatant le résultat en notation française
 * avec au plus `chiffres` chiffres significatifs (ex. avec 4 : 1000, 250,3, 48,23, 1,447).
 * Une valeur >= 1000 s'affiche donc sans décimale, une valeur proche de 1 en garde plusieurs.
 * `paliers` doit être trié du plus grand diviseur au plus petit, le dernier représentant l'unité de base (diviseur `1`).
 * Utilisé via les pipes `energieUnite`, `puissanceUnite` et `deviseUnite`.
 */
export function formaterAvecUnite(
  valeur: number | null | undefined,
  paliers: PalierUnite[],
  chiffres = 4,
): string {
  if (valeur == null || !Number.isFinite(valeur)) return '—';
  const palier = paliers.find((p) => Math.abs(valeur) >= p.diviseur) ?? paliers[paliers.length - 1];
  return `${new Intl.NumberFormat(FR, { maximumSignificantDigits: chiffres }).format(valeur / palier.diviseur)} ${palier.unite}`;
}

/** Paliers d'énergie, valeur de base en kWh. */
export const PALIERS_ENERGIE: PalierUnite[] = [
  { diviseur: 1e9, unite: 'GWh' },
  { diviseur: 1e6, unite: 'MWh' },
  { diviseur: 1e3, unite: 'kWh' },
  { diviseur: 1, unite: 'Wh' },
];

/** Paliers de puissance, valeur de base en kW. */
export const PALIERS_PUISSANCE: PalierUnite[] = [
  { diviseur: 1e9, unite: 'GW' },
  { diviseur: 1e6, unite: 'MW' },
  { diviseur: 1e3, unite: 'kW' },
  { diviseur: 1, unite: 'W' },
];

/** Paliers monétaires, valeur de base en k€ (comme `revenus_cumules` / `gainsCumules`). */
export const PALIERS_DEVISE: PalierUnite[] = [
  { diviseur: 1e6, unite: 'M€' },
  { diviseur: 1e3, unite: 'k€' },
  { diviseur: 1, unite: '€' },
];

/** Palier unique pour le CO2 évité, valeur de base en tonnes (pas de conversion d'unité). */
export const PALIERS_CO2: PalierUnite[] = [
  { diviseur: 1, unite: 't' },
];
